import { ChildSupportPayment2Dto, ChildSupportRecipientDto } from '@treasury/api/channel';
import { DomainEntity } from '../shared';
import { ChildSupportRecipient } from './child-support-recipient.entity';

export class ChildSupportPayment extends DomainEntity<ChildSupportPayment2Dto> {
    public createDefault(): this {
        return new ChildSupportPayment({
            id: 0,
            updatedDate: '',
            name: '',
            achCompanyName: '',
            achCompanyId: '',
            secCode: '',
            discretionaryData: '',
            entryDescription: '',
            restricted: false,
            debitAmount: 0,
            creditAmount: 0,
            errorMessage: '',
            transactionId: '',
            status: '',
            successMessage: '',
            numberOfApprovalsNeeded: 0,
            parentId: 0,
            batchId: 0,
            applyUpdatesToBatch: false,
            isEdited: false,
            parentFrequencyType: '',
            type: 'AchChildSupportPayment',
            canReverseFull: false,
            canReverseTransactions: false,
            achPaymentDraftId: 0,
            achPaymentTypeId: 4,
            frequency: {
                id: 0,
                updatedDate: '',
                noEndDate: false,
                repeatOnDay1: 0,
                repeatOnDay2: 0,
                type: '',
            },
            parentFrequency: {
                id: 0,
                updatedDate: '',
                noEndDate: false,
                repeatOnDay1: 0,
                repeatOnDay2: 0,
                type: '',
            },
            offsetAccount: { id: 0, updatedDate: '' },
            achCompany: {
                id: 0,
                updatedDate: '',
                companyName: '',
                companyId: '',
                entryDescription: '',
                discretionaryData: '',
                totalDailyLimit: 0,
                notOnUsTransactionTypes: '',
                batchBalanceRequirements: '',
                onUsTransactionsRequired: 0,
                onUsAmountRequiredPerBatch: 0,
                isActive: false,
                allowUnbalancedPayments: false,
                companyGuidUniqueId: '',
            },
            recipients: [],
        } as unknown as ChildSupportPayment2Dto) as this;
    }

    public get id() {
        return this.dto.id;
    }

    public get name() {
        return this.dto.name;
    }

    public get transactionId() {
        return this.dto.transactionId ?? '';
    }

    public get status() {
        return this.dto.status ?? '';
    }

    public get achCompanyName() {
        return this.dto.achCompanyName ?? '';
    }

    public get achCompanyId() {
        return this.dto.achCompanyId ?? '';
    }

    public get secCode() {
        return this.dto.secCode ?? '';
    }

    public get entryDescription() {
        return this.dto.entryDescription ?? '';
    }

    public get discretionaryData() {
        return this.dto.discretionaryData ?? '';
    }

    public get restricted() {
        return this.dto.restricted ?? false;
    }

    public get debitAmount() {
        return this.dto.debitAmount ?? 0;
    }

    public get creditAmount() {
        return this.dto.creditAmount ?? 0;
    }

    public get frequency() {
        return this.dto.frequency;
    }

    public get numberOfApprovalsNeeded() {
        return this.dto.numberOfApprovalsNeeded ?? 0;
    }

    public get permissions() {
        return this.dto.permissions ?? [];
    }

    public get userPermissions() {
        return this.dto.userPermissions ?? [];
    }

    public get audit() {
        return this.dto.audit;
    }

    public get completedApprovals() {
        return this.dto.completedApprovals ?? [];
    }

    public get availableApprovers() {
        return this.dto.availableApprovers ?? [];
    }

    public get achCompany() {
        return this.dto.achCompany;
    }

    public get rawRecipients(): ChildSupportRecipientDto[] {
        return this.dto.recipients ?? [];
    }

    public get recipients(): ChildSupportRecipient[] {
        return this.rawRecipients.map(r => new ChildSupportRecipient(r));
    }

    public get formattedCreditAmount() {
        return this.dto.creditAmount.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
        });
    }

    public get formattedDebitAmount() {
        return this.dto.debitAmount.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
        });
    }

    public hasPermission(permission: string): boolean {
        return this.userPermissions.includes(permission);
    }

    public isEditable(): boolean {
        const editableStatuses = [
            'Approval Rejected',
            'Scheduled',
            'Uninitiated',
            'Failed',
            'Expired',
            'Ready',
        ];
        return editableStatuses.includes(this.status);
    }

    public isPendingApproval(): boolean {
        return this.status === 'Pending Approval';
    }

    public wasRejected(): boolean {
        return this.status === 'Approval Rejected';
    }
}
